/**
 * Client-report taxonomies aligned with hireiq-report-mock.html + page-spec.
 * Keep FE/BE labels in sync when editing (backend: app/core/report_taxonomies.py).
 */

export const IQ_CATEGORIES = [
  'Quantitative Aptitude',
  'Logic',
  'Reasoning',
  'Data Interpretation',
  'Computer & IT Fundamentals',
  'Verbal Comprehension',
];

/** Department-keyed Work Assessment (Tech Assessment) competencies. */
export const WORK_ASSESSMENT_CATEGORIES = {
  engineering: [
    'Technology Foundation & Architecture',
    'Programming & Frameworks',
    'Problem Solving & DSA',
    'Engineering Process & Quality',
    'DevOps & Cloud Deployment',
    'AI-Driven Development',
  ],
  product: [
    'Product Strategy & Roadmapping',
    'Customer Discovery & User Research',
    'Requirements & Prioritization',
    'Product Analytics & Metrics',
    'Go-to-Market (GTM) Strategy',
    'Stakeholder Management',
  ],
  design: [
    'Design Fundamentals & Theory',
    'User Experience (UX) Research',
    'Information Architecture & Interaction Design',
    'User Interface (UI) Design & Systems',
    'Prototyping & Testing',
    'Design Handoff & Collaboration',
  ],
  data_analytics: [
    'Data Fundamentals & Querying',
    'Programming for Data (Python/R)',
    'Data Modeling & Engineering',
    'Statistical Analysis & Modeling',
    'Data Visualization & Reporting',
    'Data Governance & Privacy',
  ],
  operations: [
    'Process Design & Optimization',
    'Project & Delivery Management',
    'Supply Chain & Vendor Management',
    'Risk & Compliance Management',
    'Resource & Budget Allocation',
    'Operational Metrics & Reporting',
  ],
  customer_success: [
    'Customer Success Fundamentals',
    'Account & Relationship Management',
    'Customer Retention & Engagement',
    'Business Growth & Upselling',
    'Customer Analytics & Reporting',
    'Escalation & Crisis Management',
  ],
  branding_marketing: [
    'Marketing & Brand Fundamentals',
    'Content & Social Media Strategy',
    'SEO & Content Marketing',
    'Performance Marketing & Paid Ads',
    'Marketing Analytics & Attribution',
    'Campaign Planning & Execution',
  ],
};

/** Engineering Work Assessment competencies (demo / mocks). */
export const WORK_CATEGORIES_ENGINEERING = WORK_ASSESSMENT_CATEGORIES.engineering;

/** @deprecated Prefer WORK_ASSESSMENT_CATEGORIES by department key. */
export const WORK_CATEGORIES_STUB = WORK_ASSESSMENT_CATEGORIES.product;

/** Fixed panel factors (page-spec) — org-wide, not department-dependent. */
export const PANEL_FACTORS = [
  'Technology Foundation',
  'Design & Architecture',
  'Solutioning Mindset',
  'Engineering Process Know-how',
  'AI-Driven Development',
  'Infosec & Data Modeling',
];

/** Fixed HR behavioural factors (page-spec). */
export const HR_FACTORS = [
  'Team Collaboration',
  'Communication',
  'Adaptability & Learning Agility',
  'Leadership Potential',
  'Emotional Intelligence',
  'Cultural Fit',
];

/** Hardcoded Evaluation Summary stage weights (percent of composite). */
export const STAGE_WEIGHTS = {
  videobot: 10,
  iq: 15,
  work: 30,
  panel: 30,
  hr: 15,
};

export const REPORT_TOC_TAXONOMY = [
  { id: 'opening', label: 'Profile Summary', tagline: 'Who they are, at a glance', always: true },
  { id: 'videobot', label: 'Video Bot Screening', tagline: 'Unscripted, timed self-recorded answers' },
  { id: 'mcq', label: 'IQ Assessment', tagline: 'Objective aptitude & reasoning check' },
  { id: 'tech-assessment', label: 'Work Assessment', tagline: 'Hands-on, role-specific skill check' },
  { id: 'panel', label: 'Panel Evaluation', tagline: 'Live human panel feedback' },
  { id: 'hr-evaluation', label: 'HR Evaluation', tagline: 'Behavioral, team fit & psychology read' },
  { id: 'final-report', label: 'Evaluation Summary', tagline: 'All stages, one consolidated score' },
  { id: 'recommendation', label: 'Recommendation', tagline: 'Final call & suggested next steps', staffOnly: true },
];

/** HR Final Call dropdown — drives staff Recommendation banner title (human only). */
export const FINAL_CALL_OPTIONS = [
  { value: 'strong_recommend', label: 'Strong Recommend' },
  { value: 'recommend', label: 'Recommend' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'not_recommended', label: 'Not Recommended' },
];

export const FINAL_CALL_LABELS = Object.fromEntries(
  FINAL_CALL_OPTIONS.map((o) => [o.value, o.label]),
);
/** Score → ring colour matching hireiq-report-mock.html HR circles. */
export function hrRingColor(scoreOn5) {
  if (scoreOn5 == null || Number.isNaN(Number(scoreOn5))) return '#94a3b8';
  const s = Number(scoreOn5);
  if (s >= 4) return '#10b981';
  if (s >= 3) return '#0076d6';
  return '#d97706';
}

/** Cap AI summary to ~2 sentences for video modals. */
export function twoLineSummary(text) {
  if (!text) return '';
  const plain = String(text).replace(/\s+/g, ' ').trim();
  const sentences = plain.match(/[^.!?]+[.!?]+/g) || [plain];
  return sentences.slice(0, 2).join(' ').trim();
}

export function resolveScore(human, ai) {
  if (human != null && !Number.isNaN(Number(human))) return Number(human);
  if (ai != null && !Number.isNaN(Number(ai))) return Number(ai);
  return null;
}
